
import { User, LogOut, Pencil, Database, ArrowLeft, Lock, FileText, Sun, Moon, Terminal, CreditCard } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { ProfileEditForm } from "./ProfileEditForm";
import { ProfileInfo } from "./ProfileInfo";
import { PasswordChangeForm } from "./PasswordChangeForm";
import { useTheme, Theme } from "@/hooks/useTheme";

interface UserMenuProps {
  username: string;
}

export const UserMenu = ({ username }: UserMenuProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { theme, setTheme } = useTheme();
  const [userInfo, setUserInfo] = useState({
    email: '',
    company_name: '',
    role: '',
    username: username
  });

  useEffect(() => {
    const getUserInfo = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('email, company_name, role, username, is_admin')
          .eq('id', session.user.id)
          .maybeSingle();
        
        if (profile) {
          // Use auth metadata username as fallback if profile username equals email
          const authUsername = session.user.user_metadata?.username;
          const displayUsername = (profile.username && profile.username !== profile.email)
            ? profile.username
            : authUsername || profile.username;
          
          setUserInfo({ ...profile, username: displayUsername });
          setIsAdmin(profile.is_admin || false);
        }
      }
    };

    getUserInfo();
  }, []);

  const handleLogout = async () => {
    try {
      window.localStorage.setItem('salesby:logout-intent-at', Date.now().toString());
      window.sessionStorage.setItem('salesby:logout-intent-at', Date.now().toString());
    } catch {}
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Logged out successfully",
      });
      navigate('/login');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { return_url: window.location.origin + '/' },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (error || !data?.url) {
        toast({
          title: "Error",
          description: "Could not open subscription portal. Please try again.",
          variant: "destructive",
        });
        return;
      }
      window.open(data.url, '_blank');
    } catch {
      toast({
        title: "Error",
        description: "Could not open subscription portal. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center space-x-2 hover:bg-accent px-3 py-2 rounded-md transition-colors">
          <User className="h-4 w-4" />
          <span className="text-sm font-medium">
            {userInfo.username && userInfo.username !== userInfo.email
              ? userInfo.username
              : userInfo.email?.split('@')[0] || username}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>My Account</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {location.pathname === '/admin' && (
          <DropdownMenuItem onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span>Back to Dashboard</span>
          </DropdownMenuItem>
        )}
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogTrigger asChild>
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <Pencil className="mr-2 h-4 w-4" />
              <span>Edit Profile</span>
            </DropdownMenuItem>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Profile</DialogTitle>
            </DialogHeader>
            <ProfileEditForm
              initialData={{
                username: userInfo.username,
                company_name: userInfo.company_name || '',
                role: userInfo.role || ''
              }}
              onSuccess={() => {
                setIsEditing(false);
                const getUserInfo = async () => {
                  const { data: { session } } = await supabase.auth.getSession();
                  if (session) {
                    const { data: profile } = await supabase
                      .from('profiles')
                      .select('email, company_name, role, username, is_admin')
                      .eq('id', session.user.id)
                      .maybeSingle();
                    
                    if (profile) {
                      setUserInfo(profile);
                      setIsAdmin(profile.is_admin || false);
                    }
                  }
                };
                getUserInfo();
              }}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={isChangingPassword} onOpenChange={setIsChangingPassword}>
          <DialogTrigger asChild>
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <Lock className="mr-2 h-4 w-4" />
              <span>Change Password</span>
            </DropdownMenuItem>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            <PasswordChangeForm onSuccess={() => setIsChangingPassword(false)} />
          </DialogContent>
        </Dialog>

        <ProfileInfo userInfo={userInfo} />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/manage-subscription')}>
          <CreditCard className="mr-2 h-4 w-4" />
          <span>Manage Subscription</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">Theme</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => setTheme("light")} className={theme === "light" ? "bg-accent" : ""}>
          <Sun className="mr-2 h-4 w-4" />
          <span>Focus</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")} className={theme === "dark" ? "bg-accent" : ""}>
          <Moon className="mr-2 h-4 w-4" />
          <span>Dark</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("retro")} className={theme === "retro" ? "bg-accent" : ""}>
          <Terminal className="mr-2 h-4 w-4" />
          <span>Terminal</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a 
            href="https://www.salesby.us/privacy-policy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center cursor-pointer"
          >
            <FileText className="mr-2 h-4 w-4" />
            <span>Privacy Policy</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
